import { useEffect, useState } from "react";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Kid, Challenge, Completion } from "@starquest/core";
import styles from "./ParentHome.module.css";
import own from "./Quests.module.css";

export default function Quests() {
  const { familyId } = useAuthStore();
  const { kids, challenges, completions, setKids, setChallenges, setCompletions } = useFamilyStore();
  const theme = useTheme();

  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting,    setRejecting]    = useState(false);

  useEffect(() => {
    if (!familyId) return;
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
    api.listChallenges(familyId).then((c) => setChallenges(c as Challenge[]));
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
  }, [familyId]);

  function refresh() {
    if (!familyId) return;
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
    api.listKids(familyId).then((k) => setKids(k as Kid[]));
  }

  async function approve(subId: string) {
    if (!familyId) return;
    await api.reviewCompletion(familyId, subId, "approve");
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
  }

  async function confirmReject() {
    if (!familyId || !rejectTarget) return;
    setRejecting(true);
    await api.reviewCompletion(familyId, rejectTarget, "reject", rejectReason.trim() || undefined);
    setRejectTarget(null); setRejectReason(""); setRejecting(false);
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
  }

  function challengeNameFor(comp: Completion): string {
    return challenges.find((c) => c.challengeId === comp.challengeId)?.challengeName ?? `Challenge #${comp.completionId.slice(0, 6)}`;
  }

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>✅ {theme.challengeName} Verifications</h1>
            <div className={styles.sub}>
              {completions.length > 0
                ? `${completions.length} ${completions.length > 1 ? theme.challengeNamePlural.toLowerCase() : theme.challengeName.toLowerCase()} waiting for your verdict`
                : `All caught up — no pending ${theme.challengeNamePlural.toLowerCase()}`}
              <BottomNav />
    </div>
            <BottomNav />
    </div>
          <button className={`${styles.btn} ${styles.ghost}`} onClick={refresh}>Refresh</button>
          <BottomNav />
    </div>

        <div className={own.list}>
          {completions.length === 0 && (
            <div className={own.empty}>
              <div className={own.emptyIcon}>🎉</div>
              <div>No pending {theme.challengeNamePlural.toLowerCase()} right now</div>
              <BottomNav />
    </div>
          )}
          {completions.map((comp) => (
            <div key={comp.completionId} className={own.row}>
              <div className={own.ava}>⭐</div>
              <div className={own.info}>
                <div className={own.name}>{challengeNameFor(comp)}</div>
                <div className={own.meta}>
                  {kids.find((k) => k.kidId === comp.kidId)?.name ?? comp.kidId}
                  {" · "}{new Date(comp.submittedAt).toLocaleString()}
                  <BottomNav />
    </div>
                <BottomNav />
    </div>
              {comp.photoKey && <div className={own.photo} title="Has photo">📸</div>}
              <div className={own.btns}>
                <button className={`${own.btn} ${own.ok}`} onClick={() => approve(comp.completionId)}>✓ Approve</button>
                <button className={`${own.btn} ${own.no}`} onClick={() => { setRejectTarget(comp.completionId); setRejectReason(""); }}>✗ Reject</button>
                <BottomNav />
    </div>
              <BottomNav />
    </div>
          ))}
          <BottomNav />
    </div>

        {rejectTarget && (
          <div className={own.overlay} onClick={(e) => e.target === e.currentTarget && setRejectTarget(null)}>
            <div className={own.dialog}>
              <h3 className={own.dialogTitle}>Reject this quest?</h3>
              <p className={own.dialogSub}>Let your explorer know why so they can try again.</p>
              <textarea
                className={own.dialogTextarea}
                placeholder="e.g. The photo wasn't clear enough. Try again!"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                autoFocus
              />
              <div className={own.dialogBtns}>
                <button className={`${own.btn} ${own.no}`} onClick={confirmReject} disabled={rejecting}>
                  {rejecting ? "Rejecting…" : "✗ Reject Quest"}
                </button>
                <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setRejectTarget(null)}>Cancel</button>
                <BottomNav />
    </div>
              <BottomNav />
    </div>
            <BottomNav />
    </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
